import * as React from "react";
import { useMemo, useState, useEffect } from "react";
import { SimpleNotification } from "@/hooks/useNotifications";
import { NotificationCategory, getNotificationCategory } from "@/utils/notificationSeverity";
import { NotificationAccordionGroup } from "./NotificationAccordionGroup";
import EmptyNotifications from "./EmptyNotifications";
import type { NotificationType } from "@/types/notifications";

interface GroupedNotificationsListProps {
  notifications: SimpleNotification[];
  onDismiss: (id: string) => void;
  onMarkAsRead: (id: string) => void;
  isMobile: boolean;
}

// Priority order for groups (without 'all')
const CATEGORY_PRIORITY: NotificationCategory[] = [
  'messages',
  'connections', 
  'profile',
  'system'
];

interface GroupedNotifications {
  category: NotificationCategory;
  notifications: SimpleNotification[];
  unreadCount: number;
}

export const GroupedNotificationsList: React.FC<GroupedNotificationsListProps> = ({
  notifications,
  onDismiss,
  onMarkAsRead,
  isMobile,
}) => {
  const [openCategory, setOpenCategory] = useState<NotificationCategory | null>(null);
  const [hasInitialized, setHasInitialized] = useState(false);

  // Group notifications by category
  const groupedNotifications = useMemo((): GroupedNotifications[] => {
    const groups: Record<Exclude<NotificationCategory, 'all'>, SimpleNotification[]> = {
      messages: [],
      connections: [],
      profile: [],
      system: [],
    };

    notifications.forEach((notification) => {
      const category = getNotificationCategory(notification.notification_type as NotificationType);
      if (category !== 'all') {
        groups[category].push(notification);
      }
    });

    // Build ordered groups with unread counts
    return CATEGORY_PRIORITY
      .map((category) => ({
        category,
        notifications: groups[category],
        unreadCount: groups[category].filter((n) => !n.is_read).length,
      }))
      .filter((group) => group.notifications.length > 0);
  }, [notifications]);

  // Auto-expand first group with unread ONLY on initial load
  useEffect(() => {
    if (!hasInitialized && groupedNotifications.length > 0) {
      const firstWithUnread = groupedNotifications.find((g) => g.unreadCount > 0);
      setOpenCategory(firstWithUnread?.category || groupedNotifications[0]?.category || null);
      setHasInitialized(true);
    }
  }, [groupedNotifications, hasInitialized]);

  const handleToggle = (category: NotificationCategory) => {
    setOpenCategory((current) => (current === category ? null : category));
  };

  if (notifications.length === 0) {
    return <EmptyNotifications />;
  }

  return (
    <div className="space-y-2">
      {groupedNotifications.map((group) => (
        <NotificationAccordionGroup
          key={group.category}
          category={group.category}
          notifications={group.notifications}
          unreadCount={group.unreadCount}
          isOpen={openCategory === group.category}
          onToggle={() => handleToggle(group.category)}
          onDismiss={onDismiss}
          onMarkAsRead={onMarkAsRead}
          isMobile={isMobile}
        />
      ))}
    </div>
  );
};
